// QuotationPreview.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Divider,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
  Chip,
  Tabs,
  Tab,
  TableContainer,Table,TableBody,TableCell,TableHead,TableRow
} from '@mui/material';
import {
  ArrowBack,
  PictureAsPdf,
  Print,
  Email,
  Download,
  Share,
  CheckCircle,
  Error,
  Edit,
  Visibility,
  Send,
  AttachMoney,
  Person,
  Business,
  Receipt,
  ShoppingCart,
  LocalShipping,
  ContentCopy,
  Refresh,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const QuotationPreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [companyConfig, setCompanyConfig] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [emailDialog, setEmailDialog] = useState(false);
  const [emailData, setEmailData] = useState({
    to: '',
    subject: 'Quotation from Your Company',
    message: 'Please find the attached quotation.',
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  useEffect(() => {
    fetchQuotation();
  }, [id]);

  const fetchQuotation = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await axios.get(
        `${API_URL}/api/qt-template/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
           
          },
          withCredentials: true
        }
      );
      
      try {
        const cmpRes = await axios.get(`${API_URL}/api/cmp/${company_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (cmpRes.data?.data) setCompanyConfig(cmpRes.data.data);
      } catch(e) {}
      
      setQuotation(response.data);
    } catch (error) {
      //console.error('Error fetching quotation:', error);
      setError('Failed to load quotation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return `${companyConfig?.currency_symbol || '₹'}${(amount || 0).toLocaleString('en-IN', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  // Generate PDF
// Generate PDF
const generatePDF = () => {
  setGeneratingPDF(true);
  
  try {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    const pageWidth = pdf.internal.pageSize.getWidth(); // 210mm
    const pageHeight = pdf.internal.pageSize.getHeight(); // 297mm
    const margin = 15; // 15mm margin on all sides
    let yPos = margin;

    // ==================== HEADER SECTION ====================
    // Header background
    pdf.setFillColor(25, 118, 210);
    pdf.rect(0, 0, pageWidth, 35, 'F');
    
    pdf.setTextColor(255, 255, 255);
    
    // Left side: Company Name and Address
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.text(quotation.company_details?.name || 'Your Company', margin, 12);
    
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    
    // Filter and display address on left
    let leftY = 18;
    if (quotation.company_details?.details) {
      Object.entries(quotation.company_details.details)
        .filter(([key]) => 
          key.toLowerCase().includes('address') || 
          key.toLowerCase().includes('location') ||
          key.toLowerCase().includes('city') ||
          key.toLowerCase().includes('state') ||
          key.toLowerCase().includes('pin') ||
          key.toLowerCase().includes('zip')
        )
        .forEach(([key, value]) => {
          pdf.text(`${value}`, margin, leftY);
          leftY += 4;
        });
    }

    // Right side: Contact details
    let rightY = 18;
    if (quotation.company_details?.details) {
      Object.entries(quotation.company_details.details)
        .filter(([key]) => 
          key.toLowerCase().includes('email') || 
          key.toLowerCase().includes('contact') || 
          key.toLowerCase().includes('phone') ||
          key.toLowerCase().includes('mobile') ||
          key.toLowerCase().includes('gst') ||
          key.toLowerCase().includes('vat')
        )
        .forEach(([key, value]) => {
          const text = `${key.charAt(0).toUpperCase() + key.slice(1)}: ${value}`;
          const textWidth = pdf.getStringUnitWidth(text) * 9 / pdf.internal.scaleFactor;
          pdf.text(text, pageWidth - margin - textWidth, rightY);
          rightY += 4;
        });
    }

    // Quotation Title with underline
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text('QUOTATION', pageWidth / 2, 45, { align: 'center' });
    
    // Underline
    const titleWidth = pdf.getStringUnitWidth('QUOTATION') * 18 / pdf.internal.scaleFactor;
    pdf.line(pageWidth / 2 - titleWidth / 2, 48, pageWidth / 2 + titleWidth / 2, 48);

    yPos = 55;

    // ==================== BILL TO & QUOTATION DETAILS ====================
    const detailsStartY = yPos;
    
    // Left: Bill To
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Bill To:', margin, detailsStartY);
    
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    
    const clientDetails = [
      quotation.client_details?.name || '',
      quotation.client_details?.address || '',
      `Contact: ${quotation.client_details?.contact || ''}`,
      `Email: ${quotation.client_details?.email || ''}`,
      `GST: ${quotation.client_details?.gst || ''}`
    ].filter(item => item.trim());
    
    let clientY = detailsStartY + 6;
    clientDetails.forEach((line, index) => {
      pdf.text(line, margin + 5, clientY + (index * 4));
    });

    // Right: Quotation Details
    const rightColumnX = 120; // Position for right column
    pdf.setFont('helvetica', 'bold');
    pdf.text('Quotation Details:', rightColumnX, detailsStartY);
    
    pdf.setFont('helvetica', 'normal');
    let detailsY = detailsStartY + 6;
    
    if (quotation.quotation_details) {
      Object.entries(quotation.quotation_details).forEach(([key, value]) => {
        const label = key.replace('_', ' ').toUpperCase() + ':';
        pdf.text(label, rightColumnX, detailsY);
        pdf.text(value.toString(), pageWidth - margin, detailsY, { align: 'right' });
        detailsY += 4;
      });
    }

    yPos = Math.max(
      detailsStartY + clientDetails.length * 4 + 10,
      detailsY + 10
    );

    // Check page break
    if (yPos > pageHeight - 60) {
      pdf.addPage();
      yPos = margin;
    }

    // ==================== ITEMS TABLE ====================
    if (quotation.items && quotation.items.length > 0) {
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Items & Services', margin, yPos);
      yPos += 8;

      // Calculate available width (page width - left margin - right margin)
      const availableWidth = pageWidth - (2 * margin); // 210 - 30 = 180mm
      
      // Define columns with proper widths that fit within 180mm
      // Reduced widths to fit properly
      const columns = [
        { header: '#', dataKey: 'index', width: 8 },          // 8mm
        { header: 'Description', dataKey: 'description', width: 60 }, // 60mm
        { header: 'Unit', dataKey: 'unit', width: 12 },       // 12mm
        { header: 'Unit Price', dataKey: 'unitPrice', width: 22 }, // 22mm
        { header: 'Qty', dataKey: 'quantity', width: 12 },    // 12mm
        { header: 'Subtotal', dataKey: 'subtotal', width: 22 }, // 22mm
        { header: 'CGST%', dataKey: 'cgst', width: 15 },      // 15mm
        { header: 'SGST%', dataKey: 'sgst', width: 15 },      // 15mm
        { header: 'Amount', dataKey: 'amount', width: 22 }    // 22mm
      ];
      // Total: 8 + 60 + 12 + 22 + 12 + 22 + 15 + 15 + 22 = 188mm (within 180mm + a little overflow for borders)

      // Prepare data
      const tableData = quotation.items.map((item, index) => {
        const subtotal = (item.quantity || 1) * (item.unitPrice || 0);
        const amount = subtotal * (1 + (item.cgst || 0)/100 + (item.sgst || 0)/100);
        
        // Truncate description if too long
        let description = item.description || '';
        if (description.length > 80) {
          description = description.substring(0, 77) + '...';
        }
        
        return {
          index: index + 1,
          description: description,
          unit: item.unit || '',
          unitPrice: formatCurrency(item.unitPrice || 0),
          quantity: item.quantity || 1,
          subtotal: formatCurrency(subtotal),
          cgst: `${item.cgst || 0}%`,
          sgst: `${item.sgst || 0}%`,
          amount: formatCurrency(amount)
        };
      });

      // Create table
      pdf.autoTable({
        startY: yPos,
        head: [columns.map(col => col.header)],
        body: tableData.map(row => columns.map(col => row[col.dataKey])),
        theme: 'grid',
        headStyles: { 
          fillColor: [25, 118, 210],
          textColor: [255, 255, 255],
          fontSize: 7,
          fontStyle: 'bold',
          cellPadding: 2
        },
        styles: { 
          fontSize: 7, // Smaller font for table
          cellPadding: 2,
          overflow: 'linebreak',
          minCellHeight: 6,
          lineWidth: 0.1
        },
        columnStyles: columns.reduce((acc, col, index) => {
          acc[index] = { 
            cellWidth: col.width,
            halign: col.dataKey.includes('Price') || 
                   col.dataKey.includes('subtotal') || 
                   col.dataKey.includes('amount') ? 'right' : 
                   col.dataKey === 'index' || 
                   col.dataKey === 'quantity' ||
                   col.dataKey === 'unit' ||
                   col.dataKey === 'cgst' ||
                   col.dataKey === 'sgst' ? 'center' : 'left',
            valign: 'middle'
          };
          return acc;
        }, {}),
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
        didDrawPage: function(data) {
          // Reset yPos after table is drawn
          yPos = data.cursor.y + 10;
        }
      });

      yPos = pdf.lastAutoTable.finalY + 10;
    }

    // ==================== ADDITIONAL COSTS ====================
    const enabledCosts = quotation.additional_costs?.filter(cost => cost.enabled) || [];
    if (enabledCosts.length > 0) {
      // Check page break
      if (yPos > pageHeight - 40) {
        pdf.addPage();
        yPos = margin;
      }
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Additional Costs:', margin, yPos);
      yPos += 8;

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      
      enabledCosts.forEach((cost, index) => {
        if (yPos > pageHeight - 20) {
          pdf.addPage();
          yPos = margin;
        }
        
        pdf.text(cost.name, margin + 5, yPos);
        pdf.text(formatCurrency(cost.amount || 0), pageWidth - margin, yPos, { align: 'right' });
        yPos += 6;
        

      });

      yPos += 5;
    }

    // ==================== SUMMARY SECTION ====================
    if (quotation.totals) {
      // Check page break
      if (yPos > pageHeight - 80) {
        pdf.addPage();
        yPos = margin;
      }
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Quotation Summary', margin, yPos);
      yPos += 10;

      // Summary table - fit within margins
      const summaryWidth = 100;
      const valueWidth = 70;
      const summaryX = margin;
      
      // Summary rows
      const summaryRows = [
        ['Items Total:', formatCurrency(quotation.totals.itemsTotal || 0)],
        ['Additional Costs:', formatCurrency(quotation.totals.additionalCostsTotal || 0)],
        ['Subtotal:', formatCurrency(quotation.totals.subtotal || 0)],
      ];

      if (quotation.totals.discountAmount > 0) {
        summaryRows.push([`Discount (${quotation.totals.discountPercent || 0}%):`, `-${formatCurrency(quotation.totals.discountAmount || 0)}`]);
        summaryRows.push(['After Discount:', formatCurrency(quotation.totals.afterDiscount || 0)]);
      }

      summaryRows.push(['Total CGST:', formatCurrency(quotation.totals.cgstTotal || 0)]);
      summaryRows.push(['Total SGST:', formatCurrency(quotation.totals.sgstTotal || 0)]);
      summaryRows.push(['Total GST:', formatCurrency(quotation.totals.gstTotal || 0)]);
      summaryRows.push(['GRAND TOTAL:', formatCurrency(quotation.totals.grandTotal || 0)]);

      // Draw summary table
      pdf.setLineWidth(0.5);
      summaryRows.forEach(([label, value], index) => {
        const rowY = yPos + (index * 8);
        
        // Draw cell borders
        pdf.rect(summaryX, rowY - 6, summaryWidth + valueWidth, 8);
        
        if (label === 'GRAND TOTAL:') {
          pdf.setFillColor(240, 240, 240);
          pdf.rect(summaryX, rowY - 6, summaryWidth + valueWidth, 8, 'F');
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
        } else {
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
        }
        
        pdf.setTextColor(0, 0, 0);
        pdf.text(label, summaryX + 5, rowY);
        pdf.text(value, summaryX + summaryWidth + valueWidth - 5, rowY, { align: 'right' });
      });

      yPos += (summaryRows.length * 8) + 15;
    }

    // ==================== PAYMENT DETAILS ====================
    if (quotation.payment_details) {
      // Check page break
      if (yPos > pageHeight - 100) {
        pdf.addPage();
        yPos = margin;
      }
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Payment Details:', margin, yPos);
      yPos += 8;

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      
      // Payment option
      pdf.text(`Selected Option: ${quotation.payment_details.selectedOption?.toUpperCase() || 'Not Selected'}`, margin + 5, yPos);
      yPos += 6;
      
      if (quotation.payment_details.selectedOption === 'advance') {
        pdf.text(`Advance Payment: ${quotation.payment_details.advancePercentage || 0}% (${formatCurrency(quotation.payment_details.advanceAmount || 0)})`, margin + 5, yPos);
        yPos += 6;
        pdf.text(`Remaining Amount: ${formatCurrency(quotation.payment_details.remainingAmount || 0)}`, margin + 5, yPos);
        yPos += 6;
      }

      // Bank Details
      if (quotation.payment_details.bankDetails) {
        yPos += 5;
        pdf.setFont('helvetica', 'bold');
        pdf.text('Bank Account Details:', margin, yPos);
        yPos += 8;
        
        pdf.setFont('helvetica', 'normal');
        const bank = quotation.payment_details.bankDetails;
        
        const bankDetails = [
          `Bank: ${bank.bank_name || ''}`,
          `Account Name: ${bank.account_name || ''}`,
          `Account Number: ${bank.account_number || ''}`,
          `IFSC Code: ${bank.ifsc_code || ''}`,
          `Branch: ${bank.branch || ''}`
        ];
        
        bankDetails.forEach((detail, index) => {
          if (yPos > pageHeight - 20) {
            pdf.addPage();
            yPos = margin;
          }
          pdf.text(detail, margin + 5, yPos + (index * 6));
        });
        
        yPos += bankDetails.length * 6 + 10;
      } else {
        yPos += 10;
      }
    }

    // ==================== TERMS & CONDITIONS ====================
    const visibleTerms = quotation.terms?.filter(t => t.visible) || [];
    if (visibleTerms.length > 0) {
      // Check page break
      if (yPos > pageHeight - 60) {
        pdf.addPage();
        yPos = margin;
      }
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Terms & Conditions:', margin, yPos);
      yPos += 8;

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      
      visibleTerms.forEach((term, index) => {
        // Check if we need a new page
        if (yPos > pageHeight - 40) {
          pdf.addPage();
          yPos = margin;
          // Redraw heading on new page
          pdf.setFont('helvetica', 'bold');
          pdf.text('Terms & Conditions (continued...):', margin, yPos);
          yPos += 8;
          pdf.setFont('helvetica', 'normal');
        }
        
        // Draw term label
        pdf.setFont('helvetica', 'bold');
        pdf.text(term.label, margin+5, yPos);
        
        // Draw term content with word wrap
        const maxWidth = pageWidth - (2 * margin) - 10;
        const contentLines = pdf.splitTextToSize(term.content || '', maxWidth);
        contentLines.forEach((line, lineIndex) => {
          pdf.setFont('helvetica', 'normal');
          pdf.text(line, margin + 33, yPos + (lineIndex * 5));
        });
        
        yPos += (contentLines.length * 5) + 8;
      });
    }

    // ==================== FOOTER ====================
    // Check if we need a new page for footer
    if (yPos > pageHeight - 30) {
      pdf.addPage();
      yPos = margin;
    }
    
    pdf.setFontSize(8);
    pdf.setTextColor(150, 150, 150);
    
    // Separator line
    pdf.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 10;
    
    // Thank you message
    pdf.text('Thank you for your business!', pageWidth / 2, yPos, { align: 'center' });
    yPos += 8;
    
    // Footer details
    const footerDetails = [
      `Quotation Valid Until: ${quotation.quotation_details?.['valid_until'] || '30 days from date'}`,
      `Generated on: ${new Date().toLocaleDateString()}`,
      `Page ${pdf.internal.getNumberOfPages()} of ${pdf.internal.getNumberOfPages()}`
    ];
    
    footerDetails.forEach((detail, index) => {
      pdf.text(detail, pageWidth / 2, yPos + (index * 5), { align: 'center' });
    });

    // ==================== SAVE PDF ====================
    const quotationNumber = quotation.quotation_details?.['quotation_#'] || 
                           quotation.quotation_number || 
                           `QT${quotation.id}`;
    const fileName = `Quotation_${quotationNumber}_${quotation.client_details?.name || 'Client'}.pdf`;
    pdf.save(fileName);

    setSnackbar({
      open: true,
      message: 'PDF generated successfully!',
      severity: 'success'
    });

  } catch (error) {
    //console.error('Error generating PDF:', error);
    setSnackbar({
      open: true,
      message: 'Failed to generate PDF',
      severity: 'error'
    });
  } finally {
    setGeneratingPDF(false);
  }
};
  // Send email with quotation
  const sendEmail = async () => {
    setSendingEmail(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await axios.post(
        `${API_URL}/api/qt-template/${id}/send-email`,
        {
          email: emailData.to,
          subject: emailData.subject,
          message: emailData.message,
          quotation_id: id,
        },{
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          withCredentials: true
        }
      );
      
      setSnackbar({
        open: true,
        message: 'Email sent successfully!',
        severity: 'success'
      });
      
      setEmailDialog(false);
      // Update quotation status to 'sent'
      await updateQuotationStatus('sent');
      
    } catch (error) {
      //console.error('Error sending email:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to send email',
        severity: 'error'
      });
    } finally {
      setSendingEmail(false);
    }
  };

  // Update quotation status
  const updateQuotationStatus = async (status) => {
    try {
      const token = localStorage.getItem('token');
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      await axios.patch(
        `${API_URL}/api/qt-template/${id}/status`,
        { status }, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          withCredentials: true
        }
      ); 
      
      setQuotation(prev => ({ ...prev, status }));
    } catch (error) {
      //console.error('Error updating status:', error);
    }
  };

  // Print quotation
  const printQuotation = () => {
    window.print();
  };

  // Copy shareable link
  const copyShareLink = () => {
    const link = `${window.location.origin}/quotation-preview/${id}`;
    navigator.clipboard.writeText(link);
    setSnackbar({
      open: true,
      message: 'Link copied to clipboard!',
      severity: 'success'
    });
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading quotation...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  if (!quotation) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Quotation not found
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
            <Typography variant="h4" fontWeight="bold">
              Quotation Preview
            </Typography>
          </Box>
          
          <Chip 
            label={quotation.status.toUpperCase()} 
            color={
              quotation.status === 'draft' ? 'default' :
              quotation.status === 'sent' ? 'info' :
              quotation.status === 'accepted' ? 'success' :
              quotation.status === 'rejected' ? 'error' : 'warning'
            }
            sx={{ fontWeight: 'bold' }}
          />
        </Box>
        
        <Typography variant="subtitle1" color="text.secondary">
          Quotation: <strong>{quotation.quotation_details?.['quotation_#'] || quotation.id}</strong> | 
          Client: <strong>{quotation.client_details?.name}</strong> | 
          Date: <strong>{quotation.quotation_details?.date || new Date(quotation.createdAt).toLocaleDateString()}</strong>
        </Typography>
      </Box>

      {/* Action Buttons */}
      <Paper sx={{ p: 2, mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {/* <Button
          variant="contained"
          startIcon={<Edit />}
          onClick={() => navigate(`/quotation-from-template/edit/${id}`)}
        >
          Edit Quotation
        </Button> */}
        
        <Button
          variant="contained"
          color="secondary"
          startIcon={generatingPDF ? <CircularProgress size={20} /> : <PictureAsPdf />}
          onClick={generatePDF}
          disabled={generatingPDF}
        >
          {generatingPDF ? 'Generating...' : 'Download PDF'}
        </Button>
        
  
        
        <Button
          variant="contained"
          color="success"
          startIcon={<Email />}
          onClick={() => setEmailDialog(true)}
        >
          Send to Client
        </Button>

        
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchQuotation}
        >
          Refresh
        </Button>
      </Paper>

      {/* Tabs for different views */}
           <Tabs 
        value={activeTab} 
        onChange={(e, newValue) => setActiveTab(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 3 }}
      >
        <Tab label="Full Preview" />
        <Tab label="Summary" />
        <Tab label="Payment Details" />
      </Tabs>


      {/* Full Preview */}
      {activeTab === 0 && (
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            mb: 4,
            '@media print': {
              boxShadow: 'none',
              border: '1px solid #ddd',
            }
          }}
        >
  {/* Header */}
<Box 
  sx={{ 
    p: 3, 
    mb: 4, 
    bgcolor: quotation.company_details?.header_color || '#1976d2',
    color: 'white',
    borderRadius: 1,
  }}
>
  <Grid container spacing={3}>
    {/* Left Side - Company Name & Address */}
    <Grid item xs={12} md={6}>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        {quotation.company_details?.name || 'Your Company Name'}
      </Typography>
      
      {/* Show only address-related fields on left side */}
      {quotation.company_details?.details && 
        Object.entries(quotation.company_details.details)
          .filter(([key]) => key.toLowerCase().includes('address') || key.toLowerCase().includes('location'))
          .map(([key, value]) => (
            <Typography key={key} variant="body2">
              {key.charAt(0).toUpperCase() + key.slice(1)}: {value}
            </Typography>
          ))}
    </Grid>
    
    {/* Right Side - Contact Details */}
    <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
      {/* Show other contact details on right side */}
      {quotation.company_details?.details && 
        Object.entries(quotation.company_details.details)
          .filter(([key]) => !key.toLowerCase().includes('address') && !key.toLowerCase().includes('location'))
          .map(([key, value]) => (
            <Typography key={key} variant="body2">
              {key.charAt(0).toUpperCase() + key.slice(1)}: {value}
            </Typography>
          ))}
    </Grid>
  </Grid>


</Box>
  <Box sx={{ 
    position: 'relative', 
    textAlign: 'center', 

    // pt: 2 
  }}>
    <Typography 
      variant="h4" 
      gutterBottom 
      fontWeight="bold"
      sx={{ 
        display: 'inline-block',
        position: 'relative',
        '&:after': {
          // content: '""',
          position: 'absolute',
          left: '50%',
          bottom: '-8px',
          transform: 'translateX(-50%)',
          width: '120px',
          height: '3px',
          backgroundColor: 'white',
          borderRadius: '2px'
        }
      }}
    >
      QUOTATION
    </Typography>
  </Box>
          {/* Bill To & Quotation Details */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person /> Bill To
              </Typography>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                    {quotation.client_details.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {quotation.client_details.address}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Contact:</strong> {quotation.client_details.contact}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Email:</strong> {quotation.client_details.email}
                  </Typography>
                  <Typography variant="body2">
                    <strong>GST/VAT:</strong> {quotation.client_details.gst}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Receipt /> Quotation Details
              </Typography>
              <Card variant="outlined">
                <CardContent>
                  {quotation.quotation_details && Object.entries(quotation.quotation_details).map(([key, value]) => (
                    <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {key.replace('_', ' ').toUpperCase()}:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Items Table */}
          {quotation.items && quotation.items.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShoppingCart /> Items & Services
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#1976d2', color: 'white' }}>
                      <th style={{ padding: '12px', textAlign: 'left' }}>#</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Description</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Unit</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Unit Price</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Qty</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Subtotal</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>CGST%</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>SGST%</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotation.items.map((item, index) => {
                      const subtotal = (item.quantity || 1) * (item.unitPrice || 0);
                      const total = subtotal * (1 + (item.cgst || 0)/100 + (item.sgst || 0)/100);
                      
                      return (
                        <tr key={item.id} style={{ borderBottom: '1px solid #e0e0e0' }}>
                          <td style={{ padding: '12px' }}>{index + 1}</td>
                          <td style={{ padding: '12px' }}>{item.description}</td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>{item.unit}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>{formatCurrency(item.unitPrice)}</td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>{item.quantity}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>{formatCurrency(subtotal)}</td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>{item.cgst}%</td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>{item.sgst}%</td>
                          <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold' }}>
                            {formatCurrency(total)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </TableContainer>
            </Box>
          )}

          {/* Additional Costs */}
          {quotation.additional_costs && quotation.additional_costs.filter(c => c.enabled).length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachMoney /> Additional Costs
              </Typography>
              <Card variant="outlined">
                <CardContent>
                  {quotation.additional_costs
                    .filter(cost => cost.enabled)
                    .map((cost, index) => (
                      <Box key={cost.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">
                          {cost.name}
                          {/* {cost.description && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              ({cost.description})
                            </Typography>
                          )} */}
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(cost.amount)}
                        </Typography>
                      </Box>
                    ))}
                </CardContent>
              </Card>
            </Box>
          )}

{/* Summary Section - Professional Invoice Style */}
{quotation.totals && (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
      <Receipt /> Summary
    </Typography>
    
    <TableContainer component={Paper} variant="outlined" sx={{ border: 0 }}>
      <Table sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
        <TableBody>
          {/* Header Row */}
          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
            <TableCell colSpan={2} sx={{ borderTop: 1, borderColor: 'divider', fontWeight: 'bold' }}>
              DESCRIPTION
            </TableCell>
            <TableCell align="right" sx={{ borderTop: 1, borderColor: 'divider', fontWeight: 'bold' }}>
              AMOUNT ({companyConfig?.currency_symbol || '₹'})
            </TableCell>
          </TableRow>
          
          {/* Items Total */}
          <TableRow>
            <TableCell colSpan={2}>Items Total</TableCell>
            <TableCell align="right" fontWeight="bold">{formatCurrency(quotation.totals.itemsTotal)}</TableCell>
          </TableRow>
          
          {/* Additional Costs */}
          {quotation.totals.additionalCostsTotal > 0 && (
            <TableRow>
              <TableCell colSpan={2}>Additional Costs</TableCell>
              <TableCell align="right" fontWeight="bold">{formatCurrency(quotation.totals.additionalCostsTotal)}</TableCell>
            </TableRow>
          )}
          
          {/* Subtotal */}
          <TableRow sx={{ bgcolor: '#f9f9f9' }}>
            <TableCell colSpan={2} sx={{ fontWeight: 'bold', borderTop: 2, borderBottom: 2, borderColor: 'divider' }}>
              SUBTOTAL
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', borderTop: 2, borderBottom: 2, borderColor: 'divider' }}>
              {formatCurrency(quotation.totals.subtotal)}
            </TableCell>
          </TableRow>
          
          {/* Discount */}
          {quotation.totals.discountAmount > 0 && (
            <TableRow>
              <TableCell colSpan={2}>
                <Typography color="success.main">
                  Less: Discount ({quotation.totals.discountPercent || 0}%)
                </Typography>
              </TableCell>
              <TableCell align="right" color="success.main" fontWeight="bold">
                -{formatCurrency(quotation.totals.discountAmount)}
              </TableCell>
            </TableRow>
          )}
          
          {/* After Discount */}
          {quotation.totals.discountAmount > 0 && (
            <TableRow>
              <TableCell colSpan={2}>Amount After Discount</TableCell>
              <TableCell align="right" fontWeight="bold" color="success.main">
                {formatCurrency(quotation.totals.afterDiscount)}
              </TableCell>
            </TableRow>
          )}
          
          {/* Taxes */}
          <TableRow sx={{ bgcolor: '#e8f5e9' }}>
            <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>TAXES</TableCell>
            <TableCell></TableCell>
          </TableRow>
          
          <TableRow sx={{ bgcolor: '#f1f8e9' }}>
            <TableCell sx={{ pl: 4 }}>CGST %</TableCell>
            <TableCell></TableCell>
            <TableCell align="right">{formatCurrency(quotation.totals.cgstTotal)}</TableCell>
          </TableRow>
          
          <TableRow sx={{ bgcolor: '#f1f8e9' }}>
            <TableCell sx={{ pl: 4 }}>SGST %</TableCell>
            <TableCell></TableCell>
            <TableCell align="right">{formatCurrency(quotation.totals.sgstTotal)}</TableCell>
          </TableRow>
          
          <TableRow sx={{ bgcolor: '#e8f5e9' }}>
            <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>Total GST</TableCell>
            <TableCell align="right" fontWeight="bold">{formatCurrency(quotation.totals.gstTotal)}</TableCell>
          </TableRow>
          
          {/* Grand Total */}
          <TableRow sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            '& td': { py: 3 }
          }}>
            <TableCell colSpan={2} sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
              GRAND TOTAL
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
              {formatCurrency(quotation.totals.grandTotal)}
            </TableCell>
          </TableRow>
          
          {/* Payment Terms */}
          {quotation.paymentDetails && (
            <TableRow>
              <TableCell colSpan={3} sx={{ p: 2, bgcolor: '#fff3e0' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Payment Terms:
                </Typography>
                <Typography variant="body2">
                  {quotation.paymentDetails.selectedOption === 'advance' 
                    ? `Advance: ${quotation.paymentDetails.advancePercentage}% (${formatCurrency(quotation.paymentDetails.advanceAmount)})` +
                      ` | Balance: ${formatCurrency(quotation.paymentDetails.remainingAmount)}`
                    : 'Full Payment Upon Delivery'}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
)}
          {/* Payment Details */}
          {quotation.payment_details && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachMoney /> Payment Details
              </Typography>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom color="primary" fontWeight="bold">
                    Selected Payment Option: {quotation.payment_details.selectedOption?.toUpperCase() || 'Not Selected'}
                  </Typography>
                  
                  {quotation.payment_details.selectedOption === 'advance' && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Advance Payment:</strong> {quotation.payment_details.advancePercentage || 0}% 
                        ({formatCurrency(quotation.payment_details.advanceAmount || 0)})
                      </Typography>
                      <Typography variant="body2">
                        <strong>Remaining Amount:</strong> {formatCurrency(quotation.payment_details.remainingAmount || 0)}
                      </Typography>
                    </Box>
                  )}
                  
                  {quotation.payment_details.bankDetails && (
                    <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                        Bank Account Details:
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2">
                            <strong>Bank:</strong> {quotation.payment_details.bankDetails.bank_name}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2">
                            <strong>Account Name:</strong> {quotation.payment_details.bankDetails.account_name}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2">
                            <strong>Account No.:</strong> {quotation.payment_details.bankDetails.account_number}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2">
                            <strong>IFSC Code:</strong> {quotation.payment_details.bankDetails.ifsc_code}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            <strong>Branch:</strong> {quotation.payment_details.bankDetails.branch}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Terms & Conditions */}
          {quotation.terms && quotation.terms.filter(t => t.visible).length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalShipping /> Terms & Conditions
              </Typography>
              <Card variant="outlined">
                <CardContent>
                  {quotation.terms
                    .filter(term => term.visible)
                    .map((term, index) => (
                      <Box key={term.id} sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          {term.label}
                        </Typography>
                        <Typography variant="body2" paragraph>
                          {term.content}
                        </Typography>
                      </Box>
                    ))}
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Footer */}
          <Box sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Thank you for your business!
            </Typography>
            <Typography variant="caption" color="text.secondary">
              This quotation is valid until: {quotation.quotation_details?.valid_until || '30 days from date'}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Summary View */}
      {activeTab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Quick Summary</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                    Client Information
                  </Typography>
                  <Typography variant="body2">{quotation.client_details.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{quotation.client_details.address}</Typography>
                  <Typography variant="body2">{quotation.client_details.email}</Typography>
                  <Typography variant="body2">{quotation.client_details.contact}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                    Financial Summary
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Items Total:</Typography>
                    <Typography variant="body2" fontWeight="bold">{formatCurrency(quotation.totals?.itemsTotal || 0)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Additional Costs:</Typography>
                    <Typography variant="body2" fontWeight="bold">{formatCurrency(quotation.totals?.additional_costsTotal || 0)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">GST Total:</Typography>
                    <Typography variant="body2" fontWeight="bold">{formatCurrency(quotation.totals?.gstTotal || 0)}</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2">GRAND TOTAL:</Typography>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      {formatCurrency(quotation.totals?.grandTotal || 0)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Payment Details View */}
      {activeTab === 2 && quotation.payment_details && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Payment Information</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold" color="primary">
                    Payment Terms
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Selected Option:</strong> {quotation.payment_details.selectedOption?.toUpperCase()}
                  </Typography>
                  
                  {quotation.payment_details.selectedOption === 'advance' && (
                    <>
                      <Typography variant="body2">
                        <strong>Advance Percentage:</strong> {quotation.payment_details.advancePercentage}%
                      </Typography>
                      <Typography variant="body2">
                        <strong>Advance Amount:</strong> {formatCurrency(quotation.payment_details.advanceAmount)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Remaining Amount:</strong> {formatCurrency(quotation.payment_details.remainingAmount)}
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            {quotation.payment_details.bankDetails && (
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold" color="primary">
                      Bank Transfer Details
                    </Typography>
                    <Typography variant="body2">
                      <strong>Bank:</strong> {quotation.payment_details.bankDetails.bank_name}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Account Name:</strong> {quotation.payment_details.bankDetails.account_name}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Account Number:</strong> {quotation.payment_details.bankDetails.account_number}
                    </Typography>
                    <Typography variant="body2">
                      <strong>IFSC Code:</strong> {quotation.payment_details.bankDetails.ifsc_code}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Branch:</strong> {quotation.payment_details.bankDetails.branch}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* Email Dialog */}
      <Dialog open={emailDialog} onClose={() => setEmailDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Quotation to Client</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Client Email"
            type="email"
            fullWidth
            variant="outlined"
            value={emailData.to}
            onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="Subject"
            type="text"
            fullWidth
            variant="outlined"
            value={emailData.subject}
            onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="Message"
            type="text"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={emailData.message}
            onChange={(e) => setEmailData({ ...emailData, message: e.target.value })}
          />
          
          <Alert severity="info" sx={{ mt: 2 }}>
            The quotation PDF will be automatically attached to this email.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialog(false)}>Cancel</Button>
          <Button 
            onClick={sendEmail} 
            variant="contained" 
            disabled={sendingEmail || !emailData.to}
            startIcon={sendingEmail ? <CircularProgress size={20} /> : <Send />}
          >
            {sendingEmail ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default QuotationPreview;